import { useState, useEffect } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { FileText, Download, Calendar, Loader2 } from "lucide-react";
import { useMetrics } from "@/hooks/useMetrics";
import { useMetricsTimeSeries } from "@/hooks/useMetricsTimeSeries";
import { useAuth } from "@/hooks/useAuth";
import { useUserProfile } from "@/hooks/useUserProfile";
import { useProduct } from "@/contexts/ProductContext";
import { useProducts } from "@/hooks/useProducts";
import { generateMonthlyReport, generateQuarterlyReport, generateCustomReport } from "@/utils/reportPdfExport";
import { exportMetricsToExcel } from "@/utils/excelExport";
import { fetchMetricsData, fetchTimeSeriesData } from "@/utils/metricsCalculations";
import { CustomReportDialog, ReportConfig } from "@/components/reports/CustomReportDialog";
import { toast } from "sonner";
import { startOfMonth, endOfMonth, subMonths } from "date-fns";

const Reports = () => {
  const { user } = useAuth();
  const { profile, tenant } = useUserProfile();
  const { selectedProduct } = useProduct();
  const { products } = useProducts();
  const [isCustomDialogOpen, setIsCustomDialogOpen] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);

  useEffect(() => {
    document.title = 'Dashi | Relatórios';
  }, []);

  // Get data for last month (for monthly and quarterly reports)
  const lastMonthEnd = endOfMonth(subMonths(new Date(), 1));
  const lastMonthStart = startOfMonth(subMonths(new Date(), 1));
  const threeMonthsStart = startOfMonth(subMonths(new Date(), 3));

  // Fetch metrics for last month
  const { data: monthlyMetrics } = useMetrics({
    startDate: lastMonthStart,
    endDate: lastMonthEnd,
    productId: selectedProduct?.id,
  });

  // Fetch metrics for last quarter
  const { data: quarterlyMetrics } = useMetrics({
    startDate: threeMonthsStart,
    endDate: lastMonthEnd,
    productId: selectedProduct?.id,
  });

  // Fetch time series data
  const { data: timeSeriesData } = useMetricsTimeSeries({
    startDate: threeMonthsStart,
    endDate: lastMonthEnd,
    productId: selectedProduct?.id,
  });

  const handleMonthlyReport = async () => {
    // Validação: Verificar se há produtos
    if (!products || products.length === 0) {
      toast.error("Nenhum produto ativo encontrado. Cadastre pelo menos um produto para gerar relatórios.");
      return;
    }

    setIsGenerating(true);
    try {
      // Cenário 2: Apenas 1 produto
      if (products.length === 1) {
        const singleProduct = products[0];
        const metrics = await fetchMetricsData(lastMonthStart, lastMonthEnd, singleProduct.id);
        const timeSeries = await fetchTimeSeriesData(lastMonthStart, lastMonthEnd, singleProduct.id);
        
        await generateMonthlyReport(
          metrics,
          timeSeries.revenueData || [],
          timeSeries.churnData || [],
          profile?.full_name || user?.email || "Usuário",
          tenant?.name || "Dashi",
          tenant?.logo_url || undefined,
          singleProduct.name,
          []
        );
        
        toast.success("Relatório mensal gerado com sucesso!");
        return;
      }
      
      // Cenário 1: 2+ produtos - Buscar dados consolidados
      const consolidatedMetrics = await fetchMetricsData(lastMonthStart, lastMonthEnd, undefined);
      const consolidatedTimeSeries = await fetchTimeSeriesData(lastMonthStart, lastMonthEnd, undefined);
      
      // Buscar dados por produto
      const productDataList = await Promise.all(
        products.map(async (product) => ({
          product,
          metrics: await fetchMetricsData(lastMonthStart, lastMonthEnd, product.id),
          timeSeries: await fetchTimeSeriesData(lastMonthStart, lastMonthEnd, product.id),
        }))
      );
      
      await generateMonthlyReport(
        consolidatedMetrics,
        consolidatedTimeSeries.revenueData || [],
        consolidatedTimeSeries.churnData || [],
        profile?.full_name || user?.email || "Usuário",
        tenant?.name || "Dashi",
        tenant?.logo_url || undefined,
        undefined,
        productDataList
      );
      
      toast.success("Relatório mensal gerado com sucesso!");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao gerar relatório mensal");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleQuarterlyReport = async () => {
    // Validação: Verificar se há produtos
    if (!products || products.length === 0) {
      toast.error("Nenhum produto ativo encontrado. Cadastre pelo menos um produto para gerar relatórios.");
      return;
    }

    setIsGenerating(true);
    try {
      // Cenário 2: Apenas 1 produto
      if (products.length === 1) {
        const singleProduct = products[0];
        const metrics = await fetchMetricsData(threeMonthsStart, lastMonthEnd, singleProduct.id);
        const timeSeries = await fetchTimeSeriesData(threeMonthsStart, lastMonthEnd, singleProduct.id);
        
        await generateQuarterlyReport(
          metrics,
          timeSeries.revenueData || [],
          timeSeries.churnData || [],
          profile?.full_name || user?.email || "Usuário",
          tenant?.name || "Dashi",
          tenant?.logo_url || undefined,
          singleProduct.name,
          []
        );
        
        toast.success("Relatório trimestral gerado com sucesso!");
        return;
      }
      
      // Cenário 1: 2+ produtos - Buscar dados consolidados
      const consolidatedMetrics = await fetchMetricsData(threeMonthsStart, lastMonthEnd, undefined);
      const consolidatedTimeSeries = await fetchTimeSeriesData(threeMonthsStart, lastMonthEnd, undefined);
      
      // Buscar dados por produto
      const productDataList = await Promise.all(
        products.map(async (product) => ({
          product,
          metrics: await fetchMetricsData(threeMonthsStart, lastMonthEnd, product.id),
          timeSeries: await fetchTimeSeriesData(threeMonthsStart, lastMonthEnd, product.id),
        }))
      );
      
      await generateQuarterlyReport(
        consolidatedMetrics,
        consolidatedTimeSeries.revenueData || [],
        consolidatedTimeSeries.churnData || [],
        profile?.full_name || user?.email || "Usuário",
        tenant?.name || "Dashi",
        tenant?.logo_url || undefined,
        undefined,
        productDataList
      );
      
      toast.success("Relatório trimestral gerado com sucesso!");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao gerar relatório trimestral");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCustomReport = async (config: ReportConfig) => {
    // Validação: Verificar se há produtos
    if (!products || products.length === 0) {
      toast.error("Nenhum produto ativo encontrado. Cadastre pelo menos um produto para gerar relatórios.");
      return;
    }

    setIsGenerating(true);
    try {
      // Cenário 2: Apenas 1 produto
      if (products.length === 1) {
        const singleProduct = products[0];
        const metrics = await fetchMetricsData(config.startDate, config.endDate, singleProduct.id);
        const timeSeries = await fetchTimeSeriesData(config.startDate, config.endDate, singleProduct.id);
        
        if (!metrics) {
          toast.error("Não há dados suficientes para gerar o relatório");
          setIsGenerating(false);
          return;
        }
        
        await generateCustomReport(
          metrics,
          timeSeries.revenueData || [],
          timeSeries.churnData || [],
          config.startDate,
          config.endDate,
          profile?.full_name || user?.email || "Usuário",
          tenant?.name || "Dashi",
          tenant?.logo_url || undefined,
          singleProduct.name,
          []
        );
        
        toast.success("Relatório personalizado gerado com sucesso!");
        setIsCustomDialogOpen(false);
        return;
      }
      
      // Cenário 1: 2+ produtos - Buscar dados consolidados
      const consolidatedMetrics = await fetchMetricsData(config.startDate, config.endDate, undefined);
      const consolidatedTimeSeries = await fetchTimeSeriesData(config.startDate, config.endDate, undefined);
      
      if (!consolidatedMetrics) {
        toast.error("Não há dados suficientes para gerar o relatório");
        setIsGenerating(false);
        return;
      }
      
      // Buscar dados por produto
      const productDataList = await Promise.all(
        products.map(async (product) => ({
          product,
          metrics: await fetchMetricsData(config.startDate, config.endDate, product.id),
          timeSeries: await fetchTimeSeriesData(config.startDate, config.endDate, product.id),
        }))
      );
      
      await generateCustomReport(
        consolidatedMetrics,
        consolidatedTimeSeries.revenueData || [],
        consolidatedTimeSeries.churnData || [],
        config.startDate,
        config.endDate,
        profile?.full_name || user?.email || "Usuário",
        tenant?.name || "Dashi",
        tenant?.logo_url || undefined,
        undefined,
        productDataList
      );

      toast.success("Relatório personalizado gerado com sucesso!");
      setIsCustomDialogOpen(false);
    } catch (error) {
      console.error(error);
      toast.error("Erro ao gerar relatório personalizado");
    } finally {
      setIsGenerating(false);
    }
  };

  const handleExportExcel = async () => {
    // Validação: Verificar se há produtos
    if (!products || products.length === 0) {
      toast.error("Nenhum produto ativo encontrado. Cadastre pelo menos um produto para gerar relatórios.");
      return;
    }

    setIsGenerating(true);
    try {
      // Cenário 2: Apenas 1 produto
      if (products.length === 1) {
        const singleProduct = products[0];
        const metrics = await fetchMetricsData(threeMonthsStart, lastMonthEnd, singleProduct.id);
        const timeSeries = await fetchTimeSeriesData(threeMonthsStart, lastMonthEnd, singleProduct.id);
        
        exportMetricsToExcel({
          metrics,
          revenueTimeSeries: timeSeries.revenueData || [],
          churnTimeSeries: timeSeries.churnData || [],
          startDate: threeMonthsStart,
          endDate: lastMonthEnd,
          productName: singleProduct.name,
          productDataList: [],
        });
        
        toast.success("Dados exportados com sucesso!");
        return;
      }
      
      // Cenário 1: 2+ produtos - Buscar dados consolidados
      const consolidatedMetrics = await fetchMetricsData(threeMonthsStart, lastMonthEnd, undefined);
      const consolidatedTimeSeries = await fetchTimeSeriesData(threeMonthsStart, lastMonthEnd, undefined);
      
      // Buscar dados por produto
      const productDataList = await Promise.all(
        products.map(async (product) => ({
          product,
          metrics: await fetchMetricsData(threeMonthsStart, lastMonthEnd, product.id),
          timeSeries: await fetchTimeSeriesData(threeMonthsStart, lastMonthEnd, product.id),
        }))
      );
      
      exportMetricsToExcel({
        metrics: consolidatedMetrics,
        revenueTimeSeries: consolidatedTimeSeries.revenueData || [],
        churnTimeSeries: consolidatedTimeSeries.churnData || [],
        startDate: threeMonthsStart,
        endDate: lastMonthEnd,
        productName: undefined,
        productDataList,
      });
      
      toast.success("Dados exportados com sucesso!");
    } catch (error) {
      console.error(error);
      toast.error("Erro ao exportar dados");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <FileText className="h-8 w-8" />
            Relatórios
          </h1>
          <p className="text-muted-foreground mt-1">
            Exporte relatórios personalizados em PDF ou Excel
          </p>
        </div>

        <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
          <Card className="p-6 transition-smooth hover:shadow-primary-md">
            <div className="space-y-4">
              <div className="rounded-lg bg-primary-light p-3 w-fit">
                <FileText className="h-6 w-6 text-primary" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Relatório Mensal</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Todas as métricas do último mês
                </p>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={handleMonthlyReport}
                disabled={isGenerating || !monthlyMetrics}
              >
                {isGenerating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Gerar PDF
              </Button>
            </div>
          </Card>

          <Card className="p-6 transition-smooth hover:shadow-primary-md">
            <div className="space-y-4">
              <div className="rounded-lg bg-success/10 p-3 w-fit">
                <FileText className="h-6 w-6 text-success" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Relatório Trimestral</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Análise dos últimos 3 meses
                </p>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={handleQuarterlyReport}
                disabled={isGenerating || !quarterlyMetrics}
              >
                {isGenerating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Gerar PDF
              </Button>
            </div>
          </Card>

          <Card className="p-6 transition-smooth hover:shadow-primary-md">
            <div className="space-y-4">
              <div className="rounded-lg bg-warning/10 p-3 w-fit">
                <Calendar className="h-6 w-6 text-warning" />
              </div>
              <div>
                <h3 className="font-semibold mb-1">Relatório Personalizado</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Escolha período e métricas
                </p>
              </div>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => setIsCustomDialogOpen(true)}
                disabled={isGenerating}
              >
                <Calendar className="mr-2 h-4 w-4" />
                Configurar
              </Button>
            </div>
          </Card>
        </div>

        <Card className="p-6">
          <h3 className="text-lg font-semibold mb-4">Exportação de Dados</h3>
          <div className="space-y-4">
            <div className="flex items-center justify-between p-4 rounded-lg border">
              <div>
                <p className="font-medium">Exportar todos os dados</p>
                <p className="text-sm text-muted-foreground">
                  Baixe todas as suas métricas em formato Excel
                </p>
              </div>
              <Button
                className="gradient-primary"
                onClick={handleExportExcel}
                disabled={isGenerating || !quarterlyMetrics}
              >
                {isGenerating ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Download className="mr-2 h-4 w-4" />
                )}
                Exportar Excel
              </Button>
            </div>
          </div>
        </Card>
      </div>

      <CustomReportDialog
        open={isCustomDialogOpen}
        onOpenChange={setIsCustomDialogOpen}
        onGenerate={handleCustomReport}
        isGenerating={isGenerating}
      />
    </div>
  );
};

export default Reports;
